import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './Login.css';
import { LoadingModal } from '../components/shared/LoadingModal';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quickLoginEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_QUICK_LOGIN === 'true';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = location.state?.from?.pathname;
      
      // If no specific redirect destination, choose based on user type
      if (!from) {
        if (user?.userType === 'Supplier') {
          navigate('/supplier/quotes', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate, location, user]);

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe
      });
      
      // Navigation will be handled by the useEffect above
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Login failed. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof LoginFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleDevQuickLogin = async () => {
    setFormData({
      email: 'darren.hnatiw@zincgroup.com',
      password: 'zdn4QDV0cbz@ynb0udv',
      rememberMe: false
    });
    
    // Auto-submit with dev credentials
    setIsSubmitting(true);
    try {
      await login({
        email: 'darren.hnatiw@zincgroup.com',
        password: 'zdn4QDV0cbz@ynb0udv',
        rememberMe: false
      });
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Dev login failed. Please check if the API is running.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSupplierQuickLogin = async () => {
    setFormData({
      email: 'Charles.Chan@zinc.com.au',
      password: 'zincQuote',
      rememberMe: false
    });
    
    // Auto-submit with supplier credentials
    setIsSubmitting(true);
    try {
      await login({
        email: 'Charles.Chan@zinc.com.au',
        password: 'zincQuote',
        rememberMe: false
      });
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Supplier login failed. Please check if the API is running.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <div className="login-background">
          <div className="gradient-mesh"></div>
          <div className="geometric-pattern"></div>
        </div>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative py-12 px-4 sm:px-6 lg:px-8">
      {/* Loading overlay during login */}
      <LoadingModal
        isOpen={isSubmitting}
        message="Signing in..."
        subMessage="Please wait"
      />

      {/* Professional background */}
      <div className="login-background">
        <div className="gradient-mesh"></div>
        <div className="geometric-pattern"></div>
      </div>
      
      <div className="max-w-md w-full z-10">
        <div className="login-card fade-in">
          {/* Logo */}
          <div className="logo-container">
            <span className="logo-text">M</span>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Welcome to Mercury 2.0
            </h2>
            <p className="text-sm text-gray-600">
              Quote & Supplier Management Platform
            </p>
          </div>
          
          <form className="space-y-5" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="error-message fade-in">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{errors.general}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="form-label block text-sm font-medium mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`login-input appearance-none relative block w-full px-4 py-3 rounded-lg focus:outline-none focus:z-10 text-sm ${
                    errors.email ? 'border-red-400' : ''
                  }`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 fade-in">{errors.email}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="password" className="form-label block text-sm font-medium mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`login-input appearance-none relative block w-full px-4 py-3 rounded-lg focus:outline-none focus:z-10 text-sm ${
                    errors.password ? 'border-red-400' : ''
                  }`}
                  placeholder="Enter your password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 fade-in">{errors.password}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="custom-checkbox"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                  Remember me
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="spinner mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            {/* Development Quick Login Button */}
            {quickLoginEnabled && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <button
                  type="button"
                  onClick={handleDevQuickLogin}
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <span className="mr-2">🚀</span> Dev Quick Login
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Development only - Uses darren.hnatiw@zincgroup.com
                </p>
                
                <button
                  type="button"
                  onClick={handleSupplierQuickLogin}
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 mt-3"
                >
                  <span className="mr-2">👤</span> Supplier Quick Login
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Supplier user - Charles.Chan@zinc.com.au
                </p>
              </div>
            )}
          </form>
        </div>
        
        <p className="copyright-text">
          © 2024 Mercury 2.0. All rights reserved.
        </p>
      </div>
    </div>
  );
};
